import React, { useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, ListItemButton, ListItemIcon, ListItemText, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { HelpOutline as HelpIcon } from '@mui/icons-material';

// Aide « Comment lire les signes » : la lettre (e / ɛ, o / ɔ) donne la voyelle, l'accent donne le ton.
// Les deux sont independants (en duala, l'accent grave n'est PAS un e ouvert comme en francais).
const TEXTS = {
  fr: {
    button: 'Comment lire les signes ?',
    intro: "En duala, la lettre et l'accent disent deux choses différentes.",
    letter: 'La lettre donne la voyelle',
    letterBody: "e est fermé (comme « blé »), ɛ est ouvert (comme « père »). De même, o est fermé et ɔ est ouvert.",
    tone: "L'accent donne le ton (la hauteur de la voix)",
    toneBody: "Il se pose sur n'importe quelle voyelle. Attention : en duala, l'accent grave ne rend pas la voyelle ouverte comme en français, il marque seulement un ton bas.",
    cols: ['Écriture', 'Voyelle', 'Ton'],
    rows: [
      ['é', 'e fermé', 'haut'],
      ['è', 'e fermé', 'bas'],
      ['ɛ', 'e ouvert', 'non marqué'],
      ['ɛ́', 'e ouvert', 'haut'],
      ['ɛ̀', 'e ouvert', 'bas'],
      ['ɛ̂', 'e ouvert', 'descendant'],
      ['ɛ̌', 'e ouvert', 'montant'],
      ['ɔ́', 'o ouvert', 'haut'],
    ],
    example: "Exemple : dans kɛ́sɛ̀, la première syllabe est ouverte et haute, la seconde est ouverte et basse.",
    typing: "Au clavier, vous pouvez taper e et o : la recherche vous trouve quand même le mot.",
  },
  en: {
    button: 'How to read the signs',
    intro: 'In Duala, the letter and the accent tell two different things.',
    letter: 'The letter gives the vowel',
    letterBody: 'e is close (as in “say”), ɛ is open (as in “bed”). In the same way, o is close and ɔ is open.',
    tone: 'The accent gives the tone (the pitch of the voice)',
    toneBody: 'It can sit on any vowel. Careful: in Duala, the grave accent does not make a vowel open as in French, it only marks a low tone.',
    cols: ['Spelling', 'Vowel', 'Tone'],
    rows: [
      ['é', 'close e', 'high'],
      ['è', 'close e', 'low'],
      ['ɛ', 'open e', 'unmarked'],
      ['ɛ́', 'open e', 'high'],
      ['ɛ̀', 'open e', 'low'],
      ['ɛ̂', 'open e', 'falling'],
      ['ɛ̌', 'open e', 'rising'],
      ['ɔ́', 'open o', 'high'],
    ],
    example: 'Example: in kɛ́sɛ̀, the first syllable is open and high, the second is open and low.',
    typing: 'On your keyboard you can type e and o: the search will still find the word.',
  },
  es: {
    button: 'Cómo leer los signos',
    intro: 'En duala, la letra y el acento dicen dos cosas distintas.',
    letter: 'La letra indica la vocal',
    letterBody: 'e es cerrada (como en «seda»), ɛ es abierta (como en «perro»). Igualmente, o es cerrada y ɔ es abierta.',
    tone: 'El acento indica el tono (la altura de la voz)',
    toneBody: 'Puede ir sobre cualquier vocal. Atención: en duala, el acento grave no abre la vocal como en francés, solo marca un tono bajo.',
    cols: ['Escritura', 'Vocal', 'Tono'],
    rows: [
      ['é', 'e cerrada', 'alto'],
      ['è', 'e cerrada', 'bajo'],
      ['ɛ', 'e abierta', 'sin marcar'],
      ['ɛ́', 'e abierta', 'alto'],
      ['ɛ̀', 'e abierta', 'bajo'],
      ['ɛ̂', 'e abierta', 'descendente'],
      ['ɛ̌', 'e abierta', 'ascendente'],
      ['ɔ́', 'o abierta', 'alto'],
    ],
    example: 'Ejemplo: en kɛ́sɛ̀, la primera sílaba es abierta y alta, la segunda es abierta y baja.',
    typing: 'En el teclado puede escribir e y o: la búsqueda encuentra la palabra igualmente.',
  },
  de: {
    button: 'So liest man die Zeichen',
    intro: 'Im Duala sagen der Buchstabe und der Akzent zwei verschiedene Dinge.',
    letter: 'Der Buchstabe gibt den Vokal an',
    letterBody: 'e ist geschlossen (wie in „See“), ɛ ist offen (wie in „Bett“). Ebenso ist o geschlossen und ɔ offen.',
    tone: 'Der Akzent gibt den Ton an (die Tonhöhe der Stimme)',
    toneBody: 'Er kann auf jedem Vokal stehen. Achtung: Im Duala macht der Gravis den Vokal nicht offen wie im Französischen, er markiert nur einen tiefen Ton.',
    cols: ['Schreibung', 'Vokal', 'Ton'],
    rows: [
      ['é', 'geschlossenes e', 'hoch'],
      ['è', 'geschlossenes e', 'tief'],
      ['ɛ', 'offenes e', 'nicht markiert'],
      ['ɛ́', 'offenes e', 'hoch'],
      ['ɛ̀', 'offenes e', 'tief'],
      ['ɛ̂', 'offenes e', 'fallend'],
      ['ɛ̌', 'offenes e', 'steigend'],
      ['ɔ́', 'offenes o', 'hoch'],
    ],
    example: 'Beispiel: In kɛ́sɛ̀ ist die erste Silbe offen und hoch, die zweite offen und tief.',
    typing: 'Auf der Tastatur können Sie e und o tippen: die Suche findet das Wort trotzdem.',
  },
};

const CLOSE = { fr: 'Fermer', en: 'Close', es: 'Cerrar', de: 'Schließen' };

// Entree de la barre laterale du dictionnaire : ouvre l'aide dans une fenetre, pour ne rien
// ajouter a la page elle-meme.
function ReadingSignsHelp({ language = 'fr' }) {
  const t = TEXTS[language] || TEXTS.fr;
  const [open, setOpen] = useState(false);

  return (
    <>
      <ListItemButton onClick={() => setOpen(true)} sx={{ borderRadius: 1 }}>
        <ListItemIcon sx={{ minWidth: 32 }}>
          <HelpIcon color="primary" />
        </ListItemIcon>
        <ListItemText primary={t.button} />
      </ListItemButton>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>{t.button}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>{t.intro}</Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}><strong>{t.letter}.</strong> {t.letterBody}</Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}><strong>{t.tone}.</strong> {t.toneBody}</Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>{t.cols.map((c) => <TableCell key={c} sx={{ fontWeight: 700 }}>{c}</TableCell>)}</TableRow>
              </TableHead>
              <TableBody>
                {t.rows.map(([sign, vowel, tone]) => (
                  <TableRow key={sign}>
                    <TableCell sx={{ fontSize: '1.2rem', fontWeight: 700 }}>{sign}</TableCell>
                    <TableCell>{vowel}</TableCell>
                    <TableCell>{tone}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <Typography variant="body2" sx={{ mt: 1.5 }}>{t.example}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{t.typing}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{CLOSE[language] || CLOSE.fr}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ReadingSignsHelp;
